import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import NewOffersSwiper from "../components/NewOffersSwiper";
import { authFetch } from "../utils/authFetch";
import { BASE_URL } from "../utils/config";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ChevronLeftIcon from "../images/chevron-left-outlined-rounded.svg";
import ChevronRightIcon from "../images/chevron-right-outlined-rounded.svg";
import thumbsup from "../images/thumbsup.svg";
import TrashXmarkIcon from "../images/trash-xmark.svg";
import { useAuth } from "../context/AuthContext";
import styles from "./SearchPropertyView.module.css";

// Styled Components (similar to Home.js)
const DemandDashboardContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  min-height: 100vh;
  overflow: hidden;

  @media (max-width: 992px) {
    flex-direction: column;
    overflow-y: auto;
  }
`;

const TrendingLeftPanel = styled.div`
  border-radius: 4px;
  width: 250px;
  flex-shrink: 0;
  padding: 10px;
  background-color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border-right: 1px solid #ddd;
  align-self: flex-start;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  height: calc(110vh);
  // max-height: calc(100vh - 20px);
  box-sizing: border-box;
  scrollbar-width: none;
  -ms-overflow-style: none;
  position: relative;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (min-width: 1001px) {
    height: calc(110vh);
    // max-height: calc(100vh - 185px);
    // padding: 0;
  }

  @media (min-width: 2560px) {
    height: calc(110vh);
    // max-height: calc(100vh - 225px);
  }

  @media (min-width: 3840px) {
    height: calc(110vh);
  }

  @media (max-width: 992px) {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #ddd;
    margin-right: 0;
    margin-bottom: 10px;
    height: auto;
    max-height: none;
    overflow: hidden;
    padding-bottom: 30px;
  }

  @media (max-width: 767px) {
    padding: 10px;
    padding-bottom: 15px;
  }

  @media (max-width: 576px) {
    padding: 10px;
    padding-bottom: 25px;
  }
`;

const NewSearchesHeader = styled.div`
  padding: 15px;
  text-align: center;
  align-self: center;

  @media (max-width: 767px) {
    padding: 10px;
  }
`;

const NewSearchesTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 800;
  color: #29b185;
  margin: 0;

  @media (max-width: 767px) {
    font-size: 1.4rem;
    margin-bottom: 10px;
  }
`;

const PropertyList = styled.div`
  margin-top: -10px;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  width: 100%;
  flex: 1;
  min-height: 0;
  position: relative;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 992px) {
    height: auto;
    max-height: none;
    min-height: 400px;
  }

  @media (max-width: 767px) {
    height: auto;
    min-height: auto;
    max-height: none;
    margin-top: 0;
    padding: 10px 0;
  }

  @media (max-width: 576px) {
    height: auto;
    min-height: auto;
    max-height: none;
    padding: 5px 0;
  }
`;

const RightPanel = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: clamp(20px, 5vw, 10px);
  overflow-y: auto;
  background-color: #ffffff;

  @media (max-width: 992px) {
    padding: 20px;
  }
`;

// Keyframe animations for smooth transitions
const slideOutLeft = keyframes`
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(-100%);
  }
`;

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const PropertyCardWrapper = styled.div`
  width: 100%;
  max-width: 600px;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  padding: clamp(20px, 5vw, 30px);
  margin: 0 auto;

  /* Apply animation based on isSkipping prop */
  animation: ${props => props.$isSkipping ? slideOutLeft : slideInRight} 0.4s ease-in-out;
  animation-fill-mode: forwards;

  /* Large screens (1600px and above) */
  @media (min-width: 1600px) {
    max-width: 800px;
    padding: clamp(25px, 5vw, 40px);
    border-radius: 16px;
  }

  /* Extra large screens (2000px and above) */
  @media (min-width: 2000px) {
    max-width: 1000px;
    padding: clamp(30px, 5vw, 50px);
    border-radius: 18px;
  }

  /* Ultra wide screens (2400px and above) */
  @media (min-width: 2400px) {
    max-width: 1200px;
    padding: clamp(35px, 5vw, 60px);
    border-radius: 20px;
  }
`;

const MainImageContainer = styled.div`
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  background-color: #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 20px;

  /* Large screens */
  @media (min-width: 1600px) {
    border-radius: 10px;
    margin-bottom: 25px;
  }

  @media (min-width: 2000px) {
    border-radius: 12px;
    margin-bottom: 30px;
  }

  @media (min-width: 2400px) {
    border-radius: 14px;
    margin-bottom: 35px;
  }
`;

const MainImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
`;

const ImageNavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 12px;
  z-index: 10;
  border-radius: 50%;
  transition: background-color 0.3s ease;

  &.left {
    left: 15px;
  }

  &.right {
    right: 15px;
  }

  &:hover {
    background-color: rgba(41, 177, 133, 0.3);
  }
`;

const ImageNavIcon = styled.img`
  filter: invert(36%) sepia(35%) saturate(1637%) hue-rotate(113deg)
    brightness(88%) contrast(92%);
  width: 40px;
  height: 40px;

  /* Large screens */
  @media (min-width: 1600px) {
    width: 50px;
    height: 50px;
  }

  @media (min-width: 2000px) {
    width: 60px;
    height: 60px;
  }

  @media (min-width: 2400px) {
    width: 70px;
    height: 70px;
  }
`;

const DotIndicatorContainer = styled.div`
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;

  /* Large screens */
  @media (min-width: 1600px) {
    bottom: 15px;
    gap: 10px;
  }

  @media (min-width: 2000px) {
    bottom: 18px;
    gap: 12px;
  }

  @media (min-width: 2400px) {
    bottom: 20px;
    gap: 14px;
  }
`;

const DotIndicator = styled.div`
  width: 10px;
  height: 10px;
  background-color: ${(props) => (props.$active ? "#29B185" : "#ffffff")};
  border-radius: 50%;
  border: 1px solid #ccc;
  cursor: pointer;
  transition: background-color 0.3s ease;

  /* Large screens */
  @media (min-width: 1600px) {
    width: 12px;
    height: 12px;
  }

  @media (min-width: 2000px) {
    width: 14px;
    height: 14px;
    border: 2px solid #ccc;
  }

  @media (min-width: 2400px) {
    width: 16px;
    height: 16px;
    border: 2px solid #ccc;
  }
`;

const PropertyDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 10px;
  margin-bottom: 10px;
  background: #eaf7f3;
  border-radius: 8px;
  padding: 6px 0;
  font-size: 16px;
  font-weight: 400;
  text-align: center;

  /* Large screens */
  @media (min-width: 1600px) {
    gap: 14px;
    padding: 10px 0;
    font-size: 18px;
    border-radius: 10px;
  }

  @media (min-width: 2000px) {
    gap: 16px;
    padding: 12px 0;
    font-size: 20px;
    border-radius: 12px;
  }

  @media (min-width: 2400px) {
    gap: 18px;
    padding: 14px 0;
    font-size: 22px;
    border-radius: 14px;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    font-size: 14px;
  }
`;

const PropertyDetailsGrid2 = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 10px;
  margin-bottom: 20px;
  text-align: center;
  font-size: 16px;

  /* Large screens */
  @media (min-width: 1600px) {
    gap: 14px;
    margin-bottom: 25px;
    font-size: 18px;
  }

  @media (min-width: 2000px) {
    gap: 16px;
    margin-bottom: 30px;
    font-size: 20px;
  }

  @media (min-width: 2400px) {
    gap: 18px;
    margin-bottom: 35px;
    font-size: 22px;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    font-size: 14px;
  }
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DetailLabel = styled.span`
  color: #29b185;
  font-weight: 600;
`;

const DetailValue = styled.span`
  color: #333;
`;

const PropertyTitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 8px;

  /* Large screens */
  @media (min-width: 1600px) {
    gap: 12px;
    margin-bottom: 25px;
    padding: 20px;
    border-radius: 10px;
  }

  @media (min-width: 2000px) {
    gap: 14px;
    margin-bottom: 30px;
    padding: 25px;
    border-radius: 12px;
  }

  @media (min-width: 2400px) {
    gap: 16px;
    margin-bottom: 35px;
    padding: 30px;
    border-radius: 14px;
  }
`;

const PropertyTitleLabel = styled.span`
  font-weight: 600;
  color: #29b185;
  font-size: 1rem;

  /* Large screens */
  @media (min-width: 1600px) {
    font-size: 1.1rem;
  }

  @media (min-width: 2000px) {
    font-size: 1.2rem;
  }

  @media (min-width: 2400px) {
    font-size: 1.3rem;
  }
`;

const PropertyTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const PropertyTitleValue = styled.h3`
  font-weight: 700;
  color: #333;
  font-size: 1.3rem;
  margin: 0;
  flex: 1;

  /* Large screens */
  @media (min-width: 1600px) {
    font-size: 1.5rem;
  }

  @media (min-width: 2000px) {
    font-size: 1.7rem;
  }

  @media (min-width: 2400px) {
    font-size: 2rem;
  }
`;

const VendorNameContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
  gap: 5px;

  @media (max-width: 768px) {
    align-items: flex-start;
    text-align: left;
  }
`;

const VendorNameLabel = styled.span`
  font-weight: 600;
  color: #29b185;
  font-size: 0.9rem;

  /* Large screens */
  @media (min-width: 1600px) {
    font-size: 1rem;
  }

  @media (min-width: 2000px) {
    font-size: 1.1rem;
  }

  @media (min-width: 2400px) {
    font-size: 1.2rem;
  }
`;

const VendorNameValue = styled.span`
  color: #333;
  font-size: 1rem;
  font-weight: 500;

  /* Large screens */
  @media (min-width: 1600px) {
    font-size: 1.2rem;
  }

  @media (min-width: 2000px) {
    font-size: 1.4rem;
  }

  @media (min-width: 2400px) {
    font-size: 1.6rem;
  }
`;

const PropertyDescriptionLabel = styled.span`
  font-weight: 600;
  color: #29b185;
  font-size: 1rem;
  margin-top: 20px;
  display: block;

  /* Large screens */
  @media (min-width: 1600px) {
    font-size: 1.1rem;
    margin-top: 20px;
  }

  @media (min-width: 2000px) {
    font-size: 1.2rem;
    margin-top: 20px;
  }

  @media (min-width: 2400px) {
    font-size: 1.3rem;
    margin-top: 20px;
  }
`;

const PropertyDescriptionValue = styled.p`
  color: #555;
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0;
  min-height: 60px;

  /* Large screens */
  @media (min-width: 1600px) {
    font-size: 1.05rem;
    line-height: 1.7;
    min-height: 70px;
  }

  @media (min-width: 2000px) {
    font-size: 1.15rem;
    line-height: 1.8;
    min-height: 80px;
  }

  @media (min-width: 2400px) {
    font-size: 1.25rem;
    line-height: 1.9;
    min-height: 90px;
  }
`;

const NoPropertiesMessage = styled.div`
  text-align: center;
  font-size: 1.5rem;
  color: #555;
  padding: 50px 20px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 30px;
  border-radius: 8px;
  max-width: 400px;
  width: 90%;
  text-align: center;
`;

const ModalMessage = styled.p`
  font-size: 1rem;
  color: #333;
  margin-bottom: 20px;
`;

const ModalButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
`;

const ModalButton = styled.button`
  padding: 10px 25px;
  border-radius: 5px;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;

  &.cancel {
    background-color: #e0e0e0;
    color: #333;
  }

  &.confirm {
    background-color: #29b185;
    color: white;
  }
`;

const SearchPropertyView = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [demands, setDemands] = useState([]);
  const [offers, setOffers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [propertyType, setPropertyType] = useState(null); // 'demand' or 'offer'
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);

  // Helper function to get the appropriate room/unit label based on property type
  const getRoomLabel = (propertyType) => {
    if (!propertyType) return t("Bedrooms");
    const type = propertyType.toLowerCase();
    if (type === "multifamily") {
      return t("Units");
    }
    if (type === "plot") {
      return t("Index");
    }
    // For apartment, house/villa, and others
    return t("Bedrooms");
  };

  // Helper function to get the appropriate room/unit value based on property type
  const getRoomValue = (propertyType, bedrooms, units, index) => {
    console.log('🔍 getRoomValue called:', { propertyType, bedrooms, units, index });
    if (!propertyType) return bedrooms || units || index || "-";
    const type = propertyType.toLowerCase();
    if (type === "plot") {
      console.log('📊 Returning index for plot:', index);
      return index || "-";
    }
    if (type === "multifamily") {
      console.log('📊 Returning units for multifamily:', units);
      return units || "-";
    }
    // For apartment, house/villa, and others
    console.log('🏠 Returning bedrooms for other types:', bedrooms);
    return bedrooms || "-";
  };

  // Redirect offer users away from this page (SearchPropertyView is for demand users only)
  useEffect(() => {
    if (currentUser && currentUser.userType === 'offer') {
      toast.info(t('This_page_is_for_property_seekers_only'));
      navigate('/');
    }
  }, [currentUser, navigate, t]);

  useEffect(() => {
    const fetchRelatedProperties = async () => {
      try {
        // Fetch both demands and offers for the left panel
        const [demandsResponse, offersResponse] = await Promise.all([
          authFetch(`${BASE_URL}/property/latest/demands?limit=30`),
          authFetch(`${BASE_URL}/property/latest/offers?limit=30`),
        ]);

        if (demandsResponse.ok) {
          const contentType = demandsResponse.headers.get("content-type");
          if (contentType && contentType.indexOf("application/json") !== -1) {
            const data = await demandsResponse.json();
            setDemands(Array.isArray(data) ? data : []);
          }
        }

        if (offersResponse.ok) {
          const contentType = offersResponse.headers.get("content-type");
          if (contentType && contentType.indexOf("application/json") !== -1) {
            const data = await offersResponse.json();
            setOffers(Array.isArray(data) ? data : []);
          }
        }
      } catch (err) {
        console.error("Error fetching related properties:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRelatedProperties();
    // const intervalId = setInterval(fetchRelatedProperties, 60000);
    // return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const fetchPropertyDetails = async () => {
      if (!id) {
        setError("Property ID is missing from the URL.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setProperty(null);
      setError(null);

      try {
        // Try fetching by id first (works for both numeric id and displayId)
        let response = await authFetch(`${BASE_URL}/property/${id}`);

        // If that fails and id looks like a displayId (non-numeric), try searching
        if (!response.ok) {
          // Try searching by displayId if the direct fetch failed
          const searchResponse = await authFetch(
            `${BASE_URL}/property/search?displayId=${id}`
          );
          if (searchResponse.ok) {
            const searchData = await searchResponse.json();
            if (searchData.data && searchData.data.length > 0) {
              // Fetch the actual property by the found id
              const foundPropertyId = searchData.data[0].id;
              response = await authFetch(
                `${BASE_URL}/property/${foundPropertyId}`
              );
            }
          }
        }

        if (!response.ok) {
          console.log("Failed to fetch property details:", response.status);
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const responseData = await response.json();
        if (responseData.data) {
          // Handle nested property structure (e.g., from matching properties)
          const propertyData = responseData.data.Property || responseData.data;
          setProperty(propertyData);
          // Determine property type (demand or offer)
          const propType =
            propertyData.listType || responseData.data.listType || "offer";
          setPropertyType(propType);
        } else {
          setError("Property not found.");
        }
      } catch (err) {
        setError("Failed to fetch property details. Please try again later.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchPropertyDetails();
  }, [id]);

  useEffect(() => {
    if (property) {
      setCurrentImageIndex(0);
    }
  }, [property]);

  const handleImageSlider = (direction) => {
    if (!property?.PropertyShowcase) return;
    const allImages = [
      property.PropertyShowcase.mainImage,
      ...(property.PropertyShowcase.images || []),
    ].filter(Boolean);
    const totalImages = allImages.length;
    if (totalImages <= 1) return;

    let newIndex;
    if (direction === "left") {
      newIndex = (currentImageIndex - 1 + totalImages) % totalImages;
    } else {
      newIndex = (currentImageIndex + 1) % totalImages;
    }
    setCurrentImageIndex(newIndex);
  };

  const handleDiscoverMore = () => {
    if (!property || !currentUser) {
      toast.error(t("Please login to discover more properties"));
      return;
    }
    setShowConfirmationModal(true);
  };

  const confirmDiscoverMore = async () => {
    setShowConfirmationModal(false);

    try {
      // Get user's demand property ID
      const demandResponse = await authFetch(
        `${BASE_URL}/property/user-properties`
      );
      if (!demandResponse.ok) {
        throw new Error("Failed to fetch user properties");
      }

      const demandData = await demandResponse.json();
      const demandProperties = (demandData.data || []).filter(
        (item) => item.listType === "demand"
      );

      if (demandProperties.length === 0) {
        toast.error(t("Please create a demand property first"));
        return;
      }

      const demandPropertyId = demandProperties[0].id;
      const offerPropertyId = property.id || property.Property?.id;

      let response = await authFetch(`${BASE_URL}/property/matching/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          demandPropertyId: demandPropertyId,
          offerPropertyId: offerPropertyId,
          status: "accepted",
        }),
      });

      // If a match already exists (409), update it to "accepted" instead
      if (response.status === 409) {
        const conflictData = await response.json();
        const existingMatchId = conflictData.data?.id;
        if (existingMatchId) {
          response = await authFetch(
            `${BASE_URL}/property/matching/updateMatchStatus`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                id: existingMatchId,
                offerPropertyId: offerPropertyId,
                status: "accepted",
              }),
            }
          );
        }
      }

      if (response.ok) {
        toast.success(t("Property added successfully"));
        navigate(`/EditOrView/${offerPropertyId}`, {
          state: { fromDiscoverMore: true },
        });
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || t("Failed to add property"));
      }
    } catch (err) {
      console.error("Error creating matching property:", err);
      toast.error(t("An error occurred. Please try again."));
    }
  };

  const cancelDiscoverMore = () => {
    setShowConfirmationModal(false);
  };

  // Determine which properties to show in left panel based on current property type
  const leftPanelProperties = React.useMemo(() => {
    // If viewing a demand property, show offers in left panel
    // If viewing an offer property, show demands in left panel
    return propertyType === "demand" ? offers : demands;
  }, [propertyType, demands, offers]);

  const leftPanelTitle =
    propertyType === "demand" ? t("new_offers") : t("new_demands");

  // Find current property index in leftPanelProperties (excluding sponsor)
  const currentPropertyIndex = React.useMemo(() => {
    if (!property || !leftPanelProperties || leftPanelProperties.length === 0)
      return -1;
    const currentId = property.id;
    return leftPanelProperties.findIndex(
      (item) => item.id === currentId
    );
  }, [property, leftPanelProperties]);

  // Handle skip to next property
  const handleSkip = () => {
    if (!leftPanelProperties || leftPanelProperties.length === 0) {
      // If no properties available, stay on current property
      return;
    }

    // If current property not found in list, stay on current property
    if (currentPropertyIndex === -1) {
      return;
    }

    // Find next property index
    let nextIndex = currentPropertyIndex + 1;

    // If at the end, check if there's only one property
    if (nextIndex >= leftPanelProperties.length) {
      // If only one property or at the end, stay on same property
      if (leftPanelProperties.length === 1) {
        return;
      }
      // Otherwise loop to beginning
      nextIndex = 0;
    }

    const nextProperty = leftPanelProperties[nextIndex];
    if (nextProperty && nextProperty.id) {
      // Trigger slide-out animation
      setIsSkipping(true);

      // Wait for animation to complete before navigating
      setTimeout(() => {
        navigate(
          `/SearchPropertyView/${nextProperty.id}`,
          { state: { fromNewOffers: true } }
        );
        // Reset animation state
        setIsSkipping(false);
      }, 300); // Match animation duration
    } else {
      // If next property is invalid, stay on current property
      return;
    }
  };

  const capitalizeFirstLetter = (string) => {
    if (!string) return "";
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const formatWithApostrophe = (value) => {
    if (!value && value !== 0) return "";
    const numValue =
      typeof value === "string" ? parseFloat(value.replace(/'/g, "")) : value;
    if (isNaN(numValue)) return value;
    const digitsOnly = numValue.toString().replace(/[^\d]/g, "");
    return digitsOnly.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  };

  let rightPanelContent;

  if (loading) {
    rightPanelContent = (
      <NoPropertiesMessage>{t("Loading_Property_Data")}</NoPropertiesMessage>
    );
  } else if (error) {
    rightPanelContent = <NoPropertiesMessage>{error}</NoPropertiesMessage>;
  } else if (!property) {
    rightPanelContent = (
      <NoPropertiesMessage>
        {t("No_Property_Data_Available")}
      </NoPropertiesMessage>
    );
  } else {
    const propertyData = property;
    const address = propertyData?.PropertyAddresses?.[0] || {};
    const showcase = propertyData?.PropertyShowcase || {};
    const user = propertyData?.User || {};
    const allImages = [showcase.mainImage, ...(showcase.images || [])].filter(
      Boolean
    );
    const mainDisplayImage =
      allImages[currentImageIndex] ||
      showcase.mainImage ||
      "https://placehold.co/800x400/EEEEEE/333333?text=" +
        t("Image_Not_Available");

    rightPanelContent = (
      <PropertyCardWrapper $isSkipping={isSkipping}>
        <MainImageContainer>
          <MainImage
            src={mainDisplayImage}
            alt={t("Main_Property")}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src =
                "https://placehold.co/800x400/EEEEEE/333333?text=" +
                t("Image_Not_Available");
            }}
          />

          {allImages.length > 1 && (
            <>
              <ImageNavButton
                className="left"
                onClick={() => handleImageSlider("left")}
              >
                <ImageNavIcon src={ChevronLeftIcon} alt={t("Previous")} />
              </ImageNavButton>
              <ImageNavButton
                className="right"
                onClick={() => handleImageSlider("right")}
              >
                <ImageNavIcon src={ChevronRightIcon} alt={t("Next")} />
              </ImageNavButton>

              <DotIndicatorContainer>
                {allImages.map((_, index) => (
                  <DotIndicator
                    key={index}
                    $active={currentImageIndex === index}
                    onClick={() => setCurrentImageIndex(index)}
                  />
                ))}
              </DotIndicatorContainer>
            </>
          )}
        </MainImageContainer>

        <PropertyDetailsGrid>
          <DetailItem>
            <DetailLabel>{t("zip")}</DetailLabel>
          </DetailItem>
          <DetailItem>
            <DetailLabel>{t("city")}</DetailLabel>
          </DetailItem>
          <DetailItem>
            <DetailLabel>{t("Type")}</DetailLabel>
          </DetailItem>
          <DetailItem>
            <DetailLabel>{t("Sqm")}</DetailLabel>
          </DetailItem>
          <DetailItem>
            <DetailLabel>{getRoomLabel(propertyData.propertyType)}</DetailLabel>
          </DetailItem>
          <DetailItem>
            <DetailLabel>{t("Budget")}</DetailLabel>
          </DetailItem>
        </PropertyDetailsGrid>

        <PropertyDetailsGrid2>
          <DetailItem>
            <DetailValue>{address.zipCode || "-"}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailValue>{address.city || "-"}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailValue>{propertyData.propertyType || "-"}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailValue>{propertyData.squareMeters || "-"}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailValue>
              {getRoomValue(propertyData.propertyType, propertyData.bedrooms, propertyData.units, propertyData.index)}
            </DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailValue>
              {propertyData.price
                ? `${formatWithApostrophe(propertyData.price)} ${
                    propertyData.currency || "CHF"
                  }`
                : "-"}
            </DetailValue>
          </DetailItem>
        </PropertyDetailsGrid2>

        <PropertyTitleSection>
          <PropertyTitleLabel>{t("property_title")}</PropertyTitleLabel>
          
          <PropertyTitleRow>
            <PropertyTitleValue>
              {propertyData.propertyTitle || t("No_Title_Available")}
            </PropertyTitleValue>

            {showcase.vendorName && (
              <VendorNameContainer>
                <VendorNameLabel>{t("Vendor_Name")}</VendorNameLabel>
                <VendorNameValue>{showcase.vendorName}</VendorNameValue>
              </VendorNameContainer>
            )}
          </PropertyTitleRow>

          {propertyData.description && (
            <PropertyDescriptionLabel>
              {t("description_label")}
            </PropertyDescriptionLabel>
          )}
          <PropertyDescriptionValue>
            {propertyData.description || ''}
          </PropertyDescriptionValue>
        </PropertyTitleSection>

        <div className={styles.footerActions}>
          <button className={`${styles.actionButton} ${styles.skipButton}`} onClick={handleSkip}>
            <img
              className={styles.actionButtonIcon}
              src={TrashXmarkIcon}
              alt={t("Skip")}
            />
            <span className={styles.actionButtonText}>{t("Skip")}</span>
          </button>
          <div className={styles.userTypeBadge}>
            {user.isPrivate ? t("PRIVATE_INDIVIDUAL") : t("COMPANY_AGENCY")}
          </div>
          <button className={`${styles.actionButton} ${styles.discoverButton}`} onClick={handleDiscoverMore}>
            <img
              className={styles.actionButtonIcon}
              src={thumbsup}
              alt={t("Discover_More")}
            />
            <span className={styles.actionButtonText}>
              {t("Discover_More")}
            </span>
          </button>
        </div>
      </PropertyCardWrapper>
    );
  }

  return (
    <DemandDashboardContainer>
      <TrendingLeftPanel>
        {!isLoading && !error && leftPanelProperties.length > 0 && (
          <NewSearchesHeader>
            <NewSearchesTitle>{leftPanelTitle}</NewSearchesTitle>
          </NewSearchesHeader>
        )}
        {leftPanelProperties.length > 0 && (
          <PropertyList>
            <NewOffersSwiper items={leftPanelProperties} className="mySwiper" />
          </PropertyList>
        )}
      </TrendingLeftPanel>

      <RightPanel>{rightPanelContent}</RightPanel>

      {showConfirmationModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalMessage>
              {t(
                "Do you want to view the details? Once confirmed, your information will be shared with the property owner.",
              )}
            </ModalMessage>
            <ModalButtonContainer>
              <ModalButton className="cancel" onClick={cancelDiscoverMore}>
                {t("Cancel")}
              </ModalButton>
              <ModalButton className="confirm" onClick={confirmDiscoverMore}>
                {t("Confirm")}
              </ModalButton>
            </ModalButtonContainer>
          </ModalContent>
        </ModalOverlay>
      )}
    </DemandDashboardContainer>
  );
};

export default SearchPropertyView;
